import type { AssistantExecutionContext, AssistantExecutionResult } from './index';

const sanitizePathSegment = (value: unknown) =>
  String(value ?? '')
    .replace(/[<>:"/\\|?*\x00-\x1F]+/g, '')
    .trim();

const normalizeWindowsPath = (value: unknown) =>
  String(value ?? '')
    .replace(/\//g, '\\')
    .replace(/\\+/g, '\\')
    .trim();

const ensureSafeAbsolutePath = (value: unknown) => {
  const normalized = normalizeWindowsPath(value);
  if (!normalized) {
    throw new Error('A valid path is required.');
  }

  if (normalized.includes('..')) {
    throw new Error('Parent-directory segments are not allowed in assistant paths.');
  }

  if (!/^[A-Za-z]:\\/.test(normalized) && normalized !== 'C:\\') {
    throw new Error('Assistant paths must be absolute Windows-style paths.');
  }

  return normalized;
};

const ensureMapleWordExtension = (value: string) =>
  value.toLowerCase().endsWith('.mword') ? value : `${value}.mword`;

const joinWindowsPath = (basePath: string, segment: string) => {
  const normalizedBase = ensureSafeAbsolutePath(basePath);
  const safeSegment = sanitizePathSegment(segment);
  if (!safeSegment) {
    throw new Error('A valid document title is required.');
  }

  return normalizedBase.endsWith('\\')
    ? `${normalizedBase}${safeSegment}`
    : `${normalizedBase}\\${safeSegment}`;
};

const toMapleWordTitle = (path: string) => `MapleWord - ${path.split('\\').pop() || 'Document.mword'}`;

export async function executeMapleWordTool(
  actionId: string,
  args: Record<string, unknown>,
  ctx: AssistantExecutionContext,
): Promise<AssistantExecutionResult> {
  switch (actionId) {
    case 'create_document': {
      const documentsPath = await ctx.resolveDocumentsPath();
      const mapleWordDir = `${documentsPath}\\MapleWord`;
      const fileName = ensureMapleWordExtension(sanitizePathSegment(args.title));
      const targetPath = joinWindowsPath(mapleWordDir, fileName);
      const content = typeof args.content === 'string' && args.content.trim()
        ? args.content
        : `<h1>${fileName.replace(/\.mword$/i, '')}</h1><p></p>`;

      await ctx.ensureDir(mapleWordDir);
      const created = await ctx.createFile(targetPath, content);
      if (!created) {
        throw new Error(`Could not create the MapleWord document at ${targetPath}.`);
      }

      ctx.openApp('maple-word', toMapleWordTitle(targetPath), targetPath, content);
      return {
        message: `Created MapleWord document at ${targetPath}.`,
        shouldClose: true,
      };
    }

    case 'insert_text': {
      const targetPath = ensureSafeAbsolutePath(args.path);
      const text = typeof args.text === 'string' ? args.text : '';
      const position = args.position === 'start' ? 'start' : 'end';
      const existingContent = (await ctx.readFile(targetPath)) ?? '';
      const nextContent = position === 'start'
        ? `${text}${existingContent}`
        : `${existingContent}${text}`;

      const saved = await ctx.writeFile(targetPath, nextContent);
      if (!saved) {
        throw new Error(`Could not update the MapleWord document at ${targetPath}.`);
      }

      ctx.openApp('maple-word', toMapleWordTitle(targetPath), targetPath, nextContent);
      return {
        message: `Updated MapleWord document at ${targetPath}.`,
        shouldClose: true,
      };
    }

    case 'open_document': {
      const targetPath = ensureSafeAbsolutePath(args.path);
      const content = (await ctx.readFile(targetPath)) ?? '';
      ctx.openApp('maple-word', toMapleWordTitle(targetPath), targetPath, content);
      return {
        message: `Opened MapleWord document ${targetPath}.`,
        shouldClose: true,
      };
    }

    default:
      throw new Error(`Unsupported MapleWord action: ${actionId}.`);
  }
}
